/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { openDB, IDBPDatabase } from 'idb';
import MonsterOpen5e from 'model/formats/MonsterOpen5e';
import Spell from 'model/stats/Spell';
import Race from 'model/character/Race';
import Class from 'model/character/Class';
import Background from 'model/character/Background';
import Feat from 'model/character/Feat';
import CreatureOpen5e from 'model/formats/CreatureOpen5e';
import { fixMonsterErrors, fixCreatureErrors } from 'model/formats/KnownErrors';
import PreferenceService from './PreferenceService';

interface StoreConfig {
  name: string;
  type: string | null;
  url: string;
}

interface Manifest {
  filename: string;
  type: string;
  hash: string;
  created_at: string;
}
interface ManifestData {
  results: Array<Manifest>;
}
interface Config {
  name: string;
  type?: string;
  url?: string;
}

const DB_NAME = 'GreenGodGames';
const DB_VERSION = 10;

const sourceHost = 'https://api.open5e.com';
//const sourceHost = 'http://127.0.0.1:8000';
const STORE_CONFIGS = {
  MANIFEST:    { name: 'manifest',    type: null,         url: sourceHost + '/v1/manifest/' },
  SPELLS:      { name: 'spells',      type: 'Spell',      url: sourceHost + '/v2/spells/' },
  FEATS:       { name: 'feats',       type: 'Feat',       url: sourceHost + '/v2/feats/' },
  MONSTERS:    { name: 'monsters',    type: 'Monster',    url: sourceHost + '/v1/monsters/' },
  DOCUMENTS:   { name: 'docs',        type: null,         url: sourceHost + '/v1/documents/' },
  DOCUMENTS_V2:{ name: 'docsV2',      type: null,         url: sourceHost + '/v2/documents/' },
  BACKGROUNDS: { name: 'backgrounds', type: 'Background', url: sourceHost + '/v1/backgrounds/' }, // v2 available
  MAGIC_ITEMS: { name: 'magic_items', type: 'MagicItem',  url: sourceHost + '/v1/magicitems/' },
  RACES:       { name: 'races',       type: 'Race',       url: sourceHost + '/v1/races/' }, // v2 available
  CLASSES:     { name: 'classes',     type: 'CharClass',  url: sourceHost + '/v1/classes/' },
  // Temporarily, we are only pulling the MM2024 data from the v2/creatures list. The rest still come from v1/monsters
  // in the near future (when some bugs are fixed on open5e.com), we will pull the full set, and stop pulling the v1/monsters.
  CREATURES:   { name: 'creatures',   type: 'Creature',   url: sourceHost + '/v2/creatures/?document__key=srd-2024' },
  BRIEFS:      { name: 'briefs',      type: null,         url: null},
};

const dbPromise: Promise<IDBPDatabase<any>> = openDB(DB_NAME, DB_VERSION, {
  blocked(currentVersion, blockedVersion) {
    console.error(`IndexedDB '${DB_NAME}' upgrade blocked: another tab is holding version ${currentVersion}, this tab wants version ${blockedVersion}. Close other tabs of this app and reload.`);
  },
  blocking(currentVersion, blockedVersion) {
    console.warn(`This tab is blocking a newer version (${blockedVersion}) of '${DB_NAME}' from upgrading from ${currentVersion}. Closing the connection.`);
    // close our connection so the other tab can upgrade
    void dbPromise.then(db => db.close()).catch(() => {});
  },
  terminated() {
    console.error(`IndexedDB '${DB_NAME}' connection was terminated unexpectedly. Reload the page.`);
  },
  upgrade(db, oldVersion) {
    // v7/v8: briefs and manifest must be out-of-line key stores (no keyPath).
    // Some clients got v7 before this migration was in place and still have an inline keyPath.
    // v10: spells endpoint switched from v1 to v2 — purge stale cached spell data.
    // Also purge briefs since spell listings live there too.
    if (oldVersion < 10) {
      for (const name of ['spells', 'briefs']) {
        if (db.objectStoreNames.contains(name)) {
          db.deleteObjectStore(name);
        }
      }
    }

    for (const key in STORE_CONFIGS) {
      const store = STORE_CONFIGS[key as keyof typeof STORE_CONFIGS];
      if (typeof store !== 'string' && !db.objectStoreNames.contains(store.name)) {
        const options = ['manifest', 'briefs'].includes(store.name) ? undefined : { keyPath: 'slug' };
        db.createObjectStore(store.name, options);
      }
    }
  },
});

export default class ApiService {
  static async clearAllCaches(): Promise<void> {
    const cacheNames = Object.values(STORE_CONFIGS).map(config => typeof config === 'string' ? config : config.name);
    PreferenceService.setUserPreference('Last Api Check Date', null);
    await this.clearCaches(cacheNames);
  }

  static async clearCaches(cachesToClear: string[]): Promise<void> {
    const db = await dbPromise;
    const existing = cachesToClear.filter(name => db.objectStoreNames.contains(name));
    if (existing.length === 0) {
      console.log('clearCaches: no matching stores to clear');
      return;
    }
    const transaction = db.transaction(existing, 'readwrite');
    for (const storeName of existing) {
      await transaction.objectStore(storeName).clear();
    }
    await transaction.done;
    console.log(`Cache cleared from stores: ${existing.join(', ')}`);
  }

  static async clearStaleData(): Promise<void> {
    try {
      const response = await fetch(`${STORE_CONFIGS.MANIFEST.url}?format=json&limit=500`); // get them all back in one request
      if (!response?.json) {
        console.error('Failed to get manifest data, can\'t clear stale data in clearStaleData().');
        return;
      }
      const data: ManifestData = await response.json();
      const cachedManifestData = await this.retrieveManifestData();

      const manifestsToClear = Object.values(STORE_CONFIGS)
                                     .filter(config => config && config.type && cachedManifestData &&
                                                       this.isManifestOutdated(config, data.results,
                                                                               cachedManifestData.results))
                                     .map(config => config.name);

      if (manifestsToClear.length) {
        manifestsToClear.push(STORE_CONFIGS.BRIEFS.name);
        console.log(`Clearing caches from: ${manifestsToClear}`);
        await this.clearCaches(manifestsToClear);
      }

      await this.storeManifestData(data);
      console.log('Stale data removed from the database');
    } catch (error) {
      console.error('Failed to clear stale data:', error);
    }
  }

  private static isManifestOutdated(config: Config, currentManifest: Manifest[], cachedManifest: Manifest[]): boolean {
    const sameVersion = (config: Config, manifest: Manifest): boolean => {
      const manifestVersion = manifest.filename.match(/\bv(\d)\b/)?.[1];
      const configVersion = config.url?.match(/\bv(\d)\b/)?.[1] || 0;
      return manifestVersion == configVersion;
    }
    const getHash = (manifest: Manifest[]): string => manifest.filter(entry => (entry.type === config.type) && sameVersion(config, entry))
                                                              .map(entry => entry.hash)
                                                              .sort().join(',');
    return getHash(currentManifest) !== getHash(cachedManifest);
  }

  private static async storeManifestData(data: ManifestData): Promise<void> {
    const db = await dbPromise;
    const transaction = db.transaction(STORE_CONFIGS.MANIFEST.name, 'readwrite');
    await transaction.objectStore(STORE_CONFIGS.MANIFEST.name).put(data, 'manifest');
    await transaction.done;
  }

  private static async retrieveManifestData(): Promise<ManifestData | undefined> {
    const db = await dbPromise;
    return db.transaction(STORE_CONFIGS.MANIFEST.name)
             .objectStore(STORE_CONFIGS.MANIFEST.name)
             .get('manifest');
  }

  private static async getFromCache(key: string): Promise<any | undefined> {
    const db = await dbPromise;
    const storeName: string = STORE_CONFIGS.BRIEFS.name;
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const result = await store.get(key);
    return result?.data;
  }

  private static async addToCache(key: string, storeName: string, data: any): Promise<void> {
    if (!key) {
      if (data.key) {
        key = data.key;
      } else {
        console.error(`key not present in call to addToCache, storeName: ${storeName}`);
        return;
      }
    }
    const db = await dbPromise;
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    console.info(`putting for store ${storeName}`);
    if (storeName === STORE_CONFIGS.BRIEFS.name ||
        storeName === STORE_CONFIGS.MANIFEST.name) {
      // These stores have no keyPath — provide the key explicitly
      await store.put({ name: key, data }, key);
    } else {
      // Stores WITH a keyPath (like 'monsters') rely on 'slug'
      await store.put({ slug: key, data });
    }
    await transaction.done;
  }

  static async fetchDocuments(cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<Map<string, {name: string, licenses: string[]}> | undefined> {
    try {
      const initialSources = new Map<string, {name: string, licenses: string[]}>();
      const resultsV2: { key: string; display_name: string; licenses: {name: string; key: string}[] }[]
                     = await this.getMulti('key,display_name,licenses', STORE_CONFIGS.DOCUMENTS_V2, cacheOnly, progCallback);
      if (resultsV2?.length > 0) {
        // Maps preserve their insert order, so by adding '5e 20x4 Rules' first, it will always show at the top.
        // We sort the rest of the items alphabetically by their title:
        initialSources.set('srd-2014', {name: '', licenses: []});
        initialSources.set('srd-2024', {name: '', licenses: []});
        // Sort using natural sorting for numbers in titles
        resultsV2.sort((a, b) =>
          new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }).compare(a.display_name, b.display_name)
        );
        resultsV2.forEach((doc: { key: string; display_name: string, licenses: {name: string; key: string}[] }) => {
          initialSources.set(doc.key, {name: doc.display_name,
                                       licenses: doc.licenses.map((lic: {name: string; key: string}) => lic.key)});
        });
      }
      const results: { slug: string; title: string }[] = await this.getMulti('slug,title', STORE_CONFIGS.DOCUMENTS, cacheOnly, progCallback);
      if (results?.length > 0) {
        // Maps preserve their insert order, so by adding '5e Core Rules' first, it will always show at the top.
        // We sort the rest of the items alphabetically by their title:
        initialSources.set('wotc-srd', {name: '', licenses: []});
        // Sort using natural sorting for numbers in titles
        results.sort((a, b) =>
          new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }).compare(a.title, b.title)
        );
        results.forEach((doc: { slug: string; title: string }) => initialSources.set(doc.slug, {name: doc.title, licenses: []}));
      }
      return initialSources;
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  }

  private static async getMulti(fields: string | null, store: StoreConfig, cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<any[]> {
    const cacheName = `${store.name}_collection_${fields}`;
    const cacheResponse = await this.getFromCache(cacheName);
    if (cacheResponse) {
      console.log(`Retrieved from cache: ${cacheName}`);
      if (progCallback) {
        progCallback(cacheResponse, cacheResponse.length);
      }
      return cacheResponse;
    }

    if (cacheOnly) {
      return [];
    }

    const results = await this.fetchData(store.url, fields, 128, 1, [], progCallback);

    if (!fields && store.name !== STORE_CONFIGS.FEATS.name) {
      await Promise.all(
        results.map((result: any) => this.addToCache(result.slug, store.name, result))
      );
    }
    await this.addToCache(cacheName, STORE_CONFIGS.BRIEFS.name, results);
    // Send notification that the load is complete by passing the total count to be the actual size of the results
    // This addresses the problem of not getting back all the monsters ('loading 512 of 3540...').
    if (progCallback) {
      progCallback(results, results.length);
    }
    return results;
  }

  private static async fetchData(baseUrl: string, fields: string | null, pageSize: number, page: number, itemsSoFar: any[],
                                 progCallback?: (data: any[], totalCount: number) => void): Promise<any[]> {

    const data = await this.fetchOnePageOfData(baseUrl, fields, pageSize, page);
    if (data.results) {
      const items = [...itemsSoFar, ...data.results];
      progCallback?.(items, data.count ?? 0);

      if (data.next) {
        return this.fetchData(baseUrl, fields, pageSize, page + 1, items, progCallback);
      }
      return items;
    }
    return itemsSoFar;
  }

  private static async fetchOnePageOfData(baseUrl: string, fields: string | null, pageSize: number, page: number): Promise<any> {
    const parameters: string[] = ['format=json',
                                  fields   ? `fields=${encodeURIComponent(fields)}` : '',
                                  pageSize ? `limit=${pageSize}` : '',
                                  page     ? `page=${page}` : ''
                                 ].filter(Boolean);
    const fullUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${parameters.join('&')}`;
    try {
      const response = await fetch(fullUrl);
      if (response.ok) {
        return await response.json();
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    } catch (error: any) {
      console.error(`Fetch error for URL:${fullUrl}, error${error?.message || '???'}`);
      // Since we had an error, split the request in half:
      // This logic assumes that the first page is "page=1", not "page=0"
      if (pageSize > 1) {
        const halfPageSize = Math.floor(pageSize / 2);
        if (halfPageSize === 0) return []; // Stop splitting

        console.log(`Splitting request: ${fullUrl}`);
        const [page1Data, page2Data] = await Promise.all([
          this.fetchOnePageOfData(baseUrl, fields, halfPageSize, page * 2 - 1),
          this.fetchOnePageOfData(baseUrl, fields, halfPageSize, page * 2)
        ]);

        return {
          results: [...(page1Data?.results || []), ...(page2Data?.results || [])],
          count: Math.max(page1Data?.count || 0, page2Data?.count || 0), // Preserve total count
          next: page2Data?.next || page1Data?.next // Preserve pagination info if available
        };
      }

      console.error(`Final failure: Unable to retrieve data for URL: ${fullUrl}`);
      return { results: [], count: 0, next: null };
    }
  }

  static async getAllMonsters(   cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<MonsterOpen5e[]>  { return (await this.getMulti(null, STORE_CONFIGS.MONSTERS,    cacheOnly, progCallback) as MonsterOpen5e[]).map(fixMonsterErrors);}
  static async getAllSpells(     cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<Spell[]>          { return        this.getMulti(null, STORE_CONFIGS.SPELLS,      cacheOnly, progCallback);}
  static async getAllFeats(      cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<Feat[]>           { return        this.getMulti(null, STORE_CONFIGS.FEATS,       cacheOnly, progCallback);}
  static async getAllRaces(      cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<Race[]>           { return        this.getMulti(null, STORE_CONFIGS.RACES,       cacheOnly, progCallback);}
  static async getAllClasses(    cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<Class[]>          { return        this.getMulti(null, STORE_CONFIGS.CLASSES,     cacheOnly, progCallback);}
  static async getAllBackgrounds(cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<Background[]>     { return        this.getMulti(null, STORE_CONFIGS.BACKGROUNDS, cacheOnly, progCallback);}
  static async getAllCreatures(  cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<CreatureOpen5e[]> { return (await this.getMulti(null, STORE_CONFIGS.CREATURES,   cacheOnly, progCallback) as CreatureOpen5e[]).map(fixCreatureErrors);}

  // Fetch a SINGLE catalog monster directly from open5e by its key/slug — used to show a catalog
  // deep link (/monsterforge/cat/<key>) immediately, before the full catalog has loaded. Tries the v1
  // monster endpoint (slug) first, then the v2 creature endpoint (key). Returns a MonsterOpen5e ready
  // for Open5e.translateMonster (v2 creatures are converted to that shape), or null if not found.
  static async getMonsterByKey(key: string): Promise<MonsterOpen5e | null> {
    if (!key) {
      return null;
    }
    try {
      const resp = await fetch(`${sourceHost}/v1/monsters/${encodeURIComponent(key)}/`);
      if (resp.ok) {
        const json = await resp.json();
        if (json && json.slug) {
          return fixMonsterErrors(json as MonsterOpen5e);
        }
      }
    } catch (e) { /* fall through and try the v2 creatures endpoint */ }
    try {
      const resp = await fetch(`${sourceHost}/v2/creatures/${encodeURIComponent(key)}/`);
      if (resp.ok) {
        const json = await resp.json();
        if (json && json.key) {
          return CreatureOpen5e.convertToMonsterOpen5e(fixCreatureErrors(json as CreatureOpen5e));
        }
      }
    } catch (e) { /* not found in either endpoint */ }
    return null;
  }
  // when we pull magic items, we don't pull back everything, because we only consume: slug, document__slug, name, type & desc
  static async getMagicItems(    cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<Ability[]>        { return this.getMulti('slug,document__slug,name,type,desc', STORE_CONFIGS.MAGIC_ITEMS, cacheOnly, progCallback); }

}
